/**
 * Schema Definitions and Calculation Logic
 * 
 * Defines how the output schema is calculated for each node type based on:
 * 1. Input Schema (from previous node)
 * 2. Node Configuration (current node settings)
 */

export const SchemaDefinitions = {
    // Source Nodes
    excel_input: (config, inputSchema) => {
        // Excel input defines its own schema based on the file
        // If we have a schema in config (from file upload), use it
        if (config?.schema) {
            return config.schema.map(col => ({
                name: col.columnName || col.name,
                type: col.type || 'string',
                sampleValue: col.sampleValue
            }));
        }
        // Fallback if no file loaded yet
        return [];
    },

    table: (config, inputSchema) => {
        // Table node defines its own schema
        if (config?.selectedSchema) {
            return config.selectedSchema.map(col => ({
                name: col.columnName || col.name,
                type: col.dataType || col.type || 'string',
                sampleValue: null
            }));
        }
        return [];
    },

    // Transform Nodes
    filter_rows: (config, inputSchema) => {
        // Filter rows does not change columns, just rows
        return inputSchema || [];
    },

    sort: (config, inputSchema) => {
        // Sort does not change columns
        return inputSchema || [];
    },

    rename_column: (config, inputSchema) => {
        if (!inputSchema) return [];
        
        const { oldColumn, newColumn } = config || {};
        if (!oldColumn || !newColumn) return inputSchema;

        return inputSchema.map(col => {
            if (col.name === oldColumn) {
                return { ...col, name: newColumn };
            }
            return col;
        });
    },

    add_column: (config, inputSchema) => {
        let currentSchema = inputSchema ? [...inputSchema] : [];
        
        // Support legacy single column config
        if (config?.columnName) {
            const exists = currentSchema.find(c => c.name === config.columnName);
            if (!exists) {
                currentSchema.push({
                    name: config.columnName,
                    type: config.columnType || 'string',
                    sampleValue: null
                });
            }
        }

        // Support new multiple columns config with positioning
        if (config?.columns && Array.isArray(config.columns)) {
            config.columns.forEach(newCol => {
                if (!newCol.name) return;

                // Remove existing column if it exists (to allow overwrite/move)
                currentSchema = currentSchema.filter(c => c.name !== newCol.name);

                const schemaCol = {
                    name: newCol.name,
                    type: newCol.dataType || 'string',
                    sampleValue: null
                };

                if (newCol.position === 'start') {
                    currentSchema.unshift(schemaCol);
                } else if (newCol.position === 'after' && newCol.relativeTo) {
                    const index = currentSchema.findIndex(c => c.name === newCol.relativeTo);
                    if (index !== -1) {
                        currentSchema.splice(index + 1, 0, schemaCol);
                    } else {
                        // Fallback to end if relative column not found
                        currentSchema.push(schemaCol);
                    }
                } else {
                    // Default to end
                    currentSchema.push(schemaCol);
                }
            });
        }
        
        return currentSchema;
    },

    remove_column: (config, inputSchema) => {
        if (!inputSchema) return [];
        
        if (config?.columnName) {
            return inputSchema.filter(col => col.name !== config.columnName);
        }
        return inputSchema;
    },

    select_columns: (config, inputSchema) => {
        if (!inputSchema) return [];
        
        if (config?.selectedColumns && Array.isArray(config.selectedColumns)) {
            return inputSchema.filter(col => config.selectedColumns.includes(col.name));
        }
        return inputSchema;
    },

    join: (config, inputSchema, inputSchemasByHandle, allInputSchemas) => {
        // Resolve schemas from handles
        let leftSchema = inputSchemasByHandle?.['left'] || inputSchemasByHandle?.['dataset_left'];
        let rightSchema = inputSchemasByHandle?.['right'] || inputSchemasByHandle?.['dataset_right'];
        
        // Fallback logic when handles are missing or incomplete
        // This is critical for chained joins where handles might not be preserved or set correctly
        
        // Case 1: We have right schema (via handle) but missing left
        if (rightSchema && !leftSchema && allInputSchemas && allInputSchemas.length > 0) {
            // Find a schema that is NOT the right schema object
            const otherSchema = allInputSchemas.find(s => s !== rightSchema);
            if (otherSchema) {
                leftSchema = otherSchema;
            } else if (inputSchema && inputSchema !== rightSchema) {
                // Fallback to default inputSchema if different
                leftSchema = inputSchema;
            }
        }

        // Case 2: We have left schema (via handle) but missing right
        if (leftSchema && !rightSchema && allInputSchemas && allInputSchemas.length > 0) {
            const otherSchema = allInputSchemas.find(s => s !== leftSchema);
            if (otherSchema) {
                rightSchema = otherSchema;
            }
        }

        // Case 3: No handles at all, but we have inputs
        if (!leftSchema && !rightSchema && allInputSchemas && allInputSchemas.length >= 2) {
            // Assume first is left, second is right
            leftSchema = allInputSchemas[0];
            rightSchema = allInputSchemas[1];
        } else if (!leftSchema && !rightSchema && inputSchema) {
            // Only one input? Assume it's left
            leftSchema = inputSchema;
        }

        leftSchema = leftSchema || [];
        rightSchema = rightSchema || [];
        
        const leftPrefix = config?.leftPrefix || '';
        const rightPrefix = config?.rightPrefix || '';
        
        // Filter columns if specific ones are selected
        const leftCols = (config?.leftColumns && config.leftColumns.length > 0)
            ? leftSchema.filter(c => config.leftColumns.includes(c.name))
            : leftSchema;
            
        const rightCols = (config?.rightColumns && config.rightColumns.length > 0)
            ? rightSchema.filter(c => config.rightColumns.includes(c.name))
            : rightSchema;

        // Combine and apply prefixes
        const output = [
            ...leftCols.map(c => ({ ...c, name: `${leftPrefix}${c.name}` })),
            ...rightCols.map(c => ({ ...c, name: `${rightPrefix}${c.name}` }))
        ];
        
        return output;
    },

    // Default handler
    default: (config, inputSchema) => {
        // By default, pass input schema through
        return inputSchema || [];
    }
};

export const calculateNodeSchema = (nodeType, config, inputSchema, inputSchemasByHandle = {}, allInputSchemas = []) => {
    const calculator = SchemaDefinitions[nodeType] || SchemaDefinitions.default;
    try {
        return calculator(config, inputSchema, inputSchemasByHandle, allInputSchemas);
    } catch (error) {
        console.warn(`Error calculating schema for node type ${nodeType}:`, error);
        return [];
    }
};
